import styled from "@emotion/styled";
import React, { MouseEvent, useContext, useEffect, useRef } from "react";
import { MultiGrid } from "react-virtualized";
import { ResizeLine } from "./ResizeLine";
import { ResizeContext, ResizeContextProvider } from "./ResizeContextProvider";
import { DetailsListProps } from "./types";
import { useCellRenderer } from "./useCellRenderer";

export const DetailsList = ({
  columns,
  rows,
  width,
  height,
  sort,
  onClickHeaderCell: onClickHeader,
  onClickCell,
  bodyCellRenderer,
  headerCellRenderer,
  refMultiGrid,
  id,
  onResizeColumn,
  rowHeight = 42,
  cellClassName,
  ...props
}: DetailsListProps) => {
  const refGrid = useRef<MultiGrid | null>();

  const cellRenderer = useCellRenderer({
    columns,
    rows,
    onClickHeaderCell: onClickHeader,
    sort,
    onClickCell,
    refGrid,
    onResizeColumn,
    cellClassName
  });

  useEffect(() => {
    if (!refGrid.current) return;
    refGrid.current.recomputeGridSize();
    refGrid.current.forceUpdateGrids();
  }, [width]);

  useEffect(() => {
    if (!refGrid.current) return;
    refGrid.current.recomputeGridSize();
    refGrid.current.forceUpdateGrids();
  }, [columns]);

  const [resizeState, dispatch] = useContext(ResizeContext);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!resizeState.isResizing) return;
      dispatch({
        type: "RESIZE",
        payload: {
          mouseX: e.clientX
        }
      });
    };
    document.addEventListener("mousemove", onMouseMove as any);

    const onMouseUp = () => {
      if (!resizeState.isResizing) return;
      const deltaX = resizeState.mouseX - resizeState.startMouseX;
      if (resizeState.canResize) {
        onResizeColumn &&
          onResizeColumn({
            column: resizeState.column!,
            columnIndex: resizeState.columnIndex!,
            deltaX
          });
      }
      dispatch({
        type: "END_RESIZE"
      });
    };
    document.addEventListener("mouseup", onMouseUp);

    return () => {
      document.removeEventListener("mousemove", onMouseMove as any);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [resizeState]);

  return (
    <Root>
      <MultiGrid
        {...props}
        ref={ref => {
          refGrid.current = ref;
          if (refMultiGrid) {
            refMultiGrid(ref);
          }
        }}
        id={id}
        width={width}
        height={height}
        columnCount={columns.length}
        rowCount={rows.length + 1}
        rowHeight={rowHeight}
        fixedRowCount={1}
        enableFixedColumnScroll
        hideBottomLeftGridScrollbar
        cellRenderer={cellRenderer}
        classNameTopLeftGrid="dl-top-left-grid"
        classNameTopRightGrid="dl-top-right-grid"
        classNameBottomRightGrid="dl-bottom-right-grid"
        classNameBottomLeftGrid="dl-bottom-left-grid"
      />
      <ResizeLine />
    </Root>
  );
};

const Root = styled.div`
  position: relative;
  .ReactVirtualized__Grid {
    &:focus {
      outline: none;
    }
  }
`;

export const DetailsListWithResizeContext = (props: DetailsListProps) => {
  return (
    <ResizeContextProvider>
      <DetailsList {...props} />
    </ResizeContextProvider>
  );
};
